/**
 * LostAndFoundPanel — admin-only browser for OPEN "lost" write-offs.
 *
 * Reads GET /api/stock/write-offs?reason=lost&resolved=false and shows a
 * row per unresolved lost unit. A "Found it" action per row opens a
 * small notes prompt and calls POST /api/stock/units/{id}/recover; on
 * success the row disappears from the open list (the backend flips
 * resolved:true, so the next refetch drops it naturally).
 *
 * Sits inside StockAnalytics — the same admin surface as the other
 * backfill / reporting tools, so admins find it in one place.
 */
import React, { useCallback, useEffect, useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { Search, Loader2, CheckCircle2, PackageX, RefreshCw } from "lucide-react";
import { Button } from "../ui/button";
import { Input } from "../ui/input";
import { Label } from "../ui/label";
import { Card, CardContent, CardHeader, CardTitle } from "../ui/card";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
  DialogFooter,
} from "../ui/dialog";
import { API } from "../../App";

const fmt = (iso) => {
  if (!iso) return "—";
  try {
    return new Date(iso).toLocaleString("en-IE", {
      day: "2-digit", month: "short", hour: "2-digit", minute: "2-digit",
    });
  } catch { return iso; }
};

const LostAndFoundPanel = () => {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);

  // Recovery dialog state — one at a time, so a single set of vars covers it.
  const [recoverTarget, setRecoverTarget] = useState(null); // full write-off row
  const [recoverNotes, setRecoverNotes] = useState("");
  const [recoverSubmitting, setRecoverSubmitting] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const res = await axios.get(`${API}/stock/write-offs`, {
        params: { reason: "lost", resolved: false, days: 365 },
      });
      setItems(res.data?.items || []);
    } catch (err) {
      toast.error(err.response?.data?.detail || "Failed to load lost items");
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  const openRecover = (row) => {
    setRecoverTarget(row);
    setRecoverNotes("");
  };
  const closeRecover = () => {
    if (recoverSubmitting) return;
    setRecoverTarget(null);
  };
  const submitRecover = async () => {
    if (!recoverTarget) return;
    setRecoverSubmitting(true);
    try {
      const body = { target_location: "static" };
      if (recoverNotes.trim()) body.notes = recoverNotes.trim();
      await axios.post(
        `${API}/stock/units/${recoverTarget.unit_id}/recover`,
        body,
      );
      toast.success(`Recovered ${recoverTarget.product_name}`);
      setRecoverTarget(null);
      load();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Recovery failed");
    } finally {
      setRecoverSubmitting(false);
    }
  };

  return (
    <Card className="bg-slate-900 border-slate-700" data-testid="lost-and-found-panel">
      <CardHeader className="pb-3 flex-row items-center justify-between space-y-0">
        <CardTitle className="text-white flex items-center gap-2 text-base">
          <Search className="w-4 h-4 text-amber-400" />
          Lost &amp; Found
          <span className="text-xs font-normal text-slate-400" data-testid="lost-and-found-count">
            ({items.length} open)
          </span>
        </CardTitle>
        <Button
          size="sm"
          variant="ghost"
          onClick={load}
          disabled={loading}
          className="text-slate-400 hover:text-white"
          data-testid="lost-and-found-refresh-btn"
        >
          {loading ? <Loader2 className="w-4 h-4 animate-spin" /> : <RefreshCw className="w-4 h-4" />}
        </Button>
      </CardHeader>
      <CardContent>
        {loading && items.length === 0 && (
          <div className="flex items-center justify-center py-6">
            <Loader2 className="w-5 h-5 animate-spin text-slate-500" />
          </div>
        )}
        {!loading && items.length === 0 && (
          <p className="text-sm text-slate-500 text-center py-4" data-testid="lost-and-found-empty">
            No open lost items. Nice — everything&apos;s accounted for.
          </p>
        )}
        {items.length > 0 && (
          <div className="space-y-2">
            {items.map((row) => (
              <div
                key={row.write_off_id}
                className="flex items-center justify-between gap-3 bg-slate-800/40 rounded px-3 py-2"
                data-testid={`lost-row-${row.write_off_id}`}
              >
                <div className="min-w-0 flex-1">
                  <p className="text-white text-sm font-medium truncate">
                    {row.product_name}
                  </p>
                  <p className="text-xs text-slate-500 truncate">
                    Logged by {row.logged_by_name || "—"} · {fmt(row.logged_at)}
                    {row.notes ? ` · "${row.notes}"` : ""}
                  </p>
                </div>
                <Button
                  size="sm"
                  onClick={() => openRecover(row)}
                  className="bg-emerald-600 hover:bg-emerald-500 text-white"
                  data-testid={`lost-found-btn-${row.write_off_id}`}
                >
                  <CheckCircle2 className="w-4 h-4 mr-1" />
                  Found it
                </Button>
              </div>
            ))}
          </div>
        )}
      </CardContent>

      {/* Recovery dialog */}
      <Dialog open={!!recoverTarget} onOpenChange={(v) => { if (!v) closeRecover(); }}>
        <DialogContent className="bg-slate-900 border-slate-700 text-white" data-testid="recover-dialog">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <PackageX className="w-5 h-5 text-emerald-400" />
              Recover unit
            </DialogTitle>
            <DialogDescription className="text-slate-400 text-xs">
              {recoverTarget && (
                <>
                  Marking <span className="text-slate-200 font-semibold">{recoverTarget.product_name}</span> as
                  found — quantity will be restored from the write-off snapshot and the
                  unit will go back into static storage.
                </>
              )}
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-1">
            <Label className="text-slate-300 text-xs">Notes (optional)</Label>
            <Input
              type="text"
              value={recoverNotes}
              onChange={(e) => setRecoverNotes(e.target.value)}
              placeholder="e.g. found in van, behind toolbox"
              className="bg-slate-800 border-slate-700 text-white"
              data-testid="recover-notes-input"
            />
          </div>
          <DialogFooter className="gap-2">
            <Button
              variant="outline"
              onClick={closeRecover}
              disabled={recoverSubmitting}
              className="border-slate-700 text-slate-300"
              data-testid="recover-cancel-btn"
            >
              Cancel
            </Button>
            <Button
              onClick={submitRecover}
              disabled={recoverSubmitting}
              className="bg-emerald-600 hover:bg-emerald-500 text-white font-semibold"
              data-testid="recover-confirm-btn"
            >
              {recoverSubmitting ? <Loader2 className="w-4 h-4 animate-spin" /> : "Confirm recovery"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </Card>
  );
};

export default LostAndFoundPanel;
